import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { getHormonePatternContentById } from '../../services/admin/admin-hormone-pattern-content.service';
import styled from 'styled-components';
import Variables from '../../design-system/variables';
import OvBackButton from '../UI/atoms/OvBackButton';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import breakpoints from '../../design-system/breakpoints';
import OvTable from '../UI/molecules/OvTable';
import { HormonePatternContentDataUtils } from '../../common/utils/services/hormone-pattern-content-data-utils';
import { useAdminHormonePatternContentMutations } from '../../hooks/use-admin-hormone-pattern-content-mutations';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';

const HormonePatternContentDetails = () => {
  const { t } = useTranslation();
  const { hormonePatternContentId }: any = useParams();

  const {
    data: hormmonePatternContent,
    isPending: isGetHormonePatternContentPending,
    isFetching: isGetHormonePatternContentFetching,
    error: getHormonePatternContentError,
  } = useQuery({
    queryKey: ['admin-hormone-pattern-content', hormonePatternContentId],
    queryFn: () => getHormonePatternContentById(hormonePatternContentId),
    retry: 3,
    enabled: !!hormonePatternContentId,
  });

  const { updateHormonePatternContent } =
    useAdminHormonePatternContentMutations(hormonePatternContentId!);

  const isLoading =
    isGetHormonePatternContentFetching || isGetHormonePatternContentPending;

  const allErrors = [
    getHormonePatternContentError,
    updateHormonePatternContent.error,
  ].filter((e): e is Error => !!e);

  return (
    <DetailPageWrapper>
      <DetailsHeader>
        <DetailsWrapper>
          <OvBackButton label={t('common.actions.back')} />
          {`${hormmonePatternContent?.name} (${hormmonePatternContent?.key})`}
        </DetailsWrapper>

        <span>
          {allErrors.map((err, i) => (
            <OvErrorMessage key={i} message={err.message} />
          ))}
        </span>
      </DetailsHeader>

      <DetailPageContentWrapper>
        <TableWrapper>
          <OvTable
            data={HormonePatternContentDataUtils.mapHormonePatternContentToTableData(
              hormmonePatternContent
            )}
            onSaveRowValue={updateHormonePatternContent.mutate}
            editable={true}
          />
        </TableWrapper>
      </DetailPageContentWrapper>

      {isLoading && <OvLoadingIndicator position="fixed" />}
    </DetailPageWrapper>
  );
};

export default HormonePatternContentDetails;

const DetailPageWrapper = styled.div`
  margin-left: 1rem;
  border-radius: ${Variables.borderRadius.SMALL};
`;

const DetailsHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  font-weight: bold;
`;

const DetailsWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const DetailPageContentWrapper = styled.div`
  display: flex;
  @media (max-width: ${breakpoints.md}) {
    flex-direction: column;
  }
`;

const TableWrapper = styled.div`
  flex-grow: 1;
  align-self: flex-start;
  margin-left: 12px;
  width: 100%;

  @media (max-width: ${breakpoints.md}) {
    margin-left: 0;
    margin-top: 12px;
    margin-bottom: 12px;
  }
`;
